import type { components } from '@connected/api-client/api.generated';

import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';

import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';

import type { ClassificationData, ObservationClassificationCodeKinds } from './observation-types';

import { OBSERVATION_COLOR_CODES } from './observation-constants';
import { getClassificationCodeData, getClassificationGroupData, isDistributionBoardObservationKind, isFinalCircuitObservationKind } from './observation-utils';

type ObservationData = components['schemas']['ListElectricalProjectObservationQueryResult'];
type ObservationDataItem = components['schemas']['ListElectricalProjectObservationQueryResultObservation'];
type SummaryData = components['schemas']['ViewElectricalProjectSummaryQueryResult'];

export function updateObservationListCache(params: {
    electricalProjectVersionId: string;
    action: 'create' | 'update' | 'delete';
    observationData: ObservationDataItem;
    observationId: string;
}) {
    const { electricalProjectVersionId, action, observationData, observationId } = params;
    const observationListQuery = elektraAppQueryOptions.project.observationsListOptions({
        electricalProjectVersionId,
    });

    const previousData = queryClient.getQueryData<ObservationData>(observationListQuery.queryKey);

    if (!previousData) {
        logger.warn('No previous observation list data found in cache.');
        return previousData;
    }

    logger.info({
        message: `${action === 'create' ? 'Adding' : action === 'update' ? 'Updating' : 'Deleting'} observation in cache:`,
    });

    if (action === 'create') {
        logger.info({
            message: 'Adding observation to cache:',
            data: observationData,
        });
        queryClient.setQueryData<ObservationData>(observationListQuery.queryKey, {
            ...previousData,
            Observations: [...previousData.Observations, observationData],
        });
        logger.info({
            message: 'Added observation to cache:',
            data: observationData,
        });
        logger.info({
            message: 'New observation list:',
            data: queryClient.getQueryData<ObservationData>(observationListQuery.queryKey),
        });
    }
    else if (action === 'update') {
        queryClient.setQueryData<ObservationData>(observationListQuery.queryKey, {
            ...previousData,
            Observations: previousData.Observations.map((obs) => {
                if (obs.ElectricalProjectObservationId === observationId) {
                    // Filter out null/undefined values to avoid overwriting valid fields like ObjectKind, ID fields
                    const definedOptimisticData = Object.fromEntries(
                        Object.entries(observationData)?.filter(([_, value]) => value != null),
                    );
                    return {
                        ...obs,
                        ...definedOptimisticData,
                    };
                }
                return obs;
            }),
        });
    }
    else if (action === 'delete') {
        queryClient.setQueryData<ObservationData>(observationListQuery.queryKey, {
            ...previousData,
            Observations: previousData.Observations?.filter(
                obs => obs.ElectricalProjectObservationId !== observationId,
            ),
            Count: (previousData.Count || 1) - 1,
        });
    }

    return previousData;
}

export function updateSummaryCacheOnCreate(params: {
    electricalProjectVersionId: string;
    codeKind: 'Remark' | 'Severe' | 'Serious' | 'Minor' | null | undefined;
}) {
    const { electricalProjectVersionId, codeKind } = params;
    const summaryOptions = elektraAppQueryOptions.project.recapViewOptions({
        electricalProjectVersionId,
    });

    queryClient.setQueryData<SummaryData>(summaryOptions.queryKey, (old) => {
        if (old && old.Summary) {
            return {
                ...old,
                Summary: {
                    ...old.Summary,
                    ElectricalProjectVersionId: old.Summary.ElectricalProjectVersionId,
                    Observations: old?.Summary?.Observations?.map((observation) => {
                        if (observation.Code === codeKind) {
                            return {
                                ...observation,
                                Count: (observation?.Count || 0) + 1,
                            };
                        }
                        return observation;
                    }),
                },
            };
        }
        return old;
    });
}

export function updateSummaryCacheOnUpdate(params: {
    electricalProjectVersionId: string;
    newCodeKind: 'Remark' | 'Severe' | 'Serious' | 'Minor' | null | undefined;
    previousCodeKind: 'Remark' | 'Severe' | 'Serious' | 'Minor' | null | undefined;
}) {
    const { electricalProjectVersionId, newCodeKind, previousCodeKind } = params;
    const summaryOptions = elektraAppQueryOptions.project.recapViewOptions({
        electricalProjectVersionId,
    });

    queryClient.setQueryData<SummaryData>(summaryOptions.queryKey, (old) => {
        if (old && old.Summary) {
            return {
                ...old,
                Summary: {
                    ...old.Summary,
                    ElectricalProjectVersionId: old.Summary.ElectricalProjectVersionId,
                    Observations: old?.Summary?.Observations?.map((observation) => {
                        if (observation.Code === newCodeKind) {
                            return {
                                ...observation,
                                Count: (observation?.Count || 0) + 1,
                            };
                        }
                        else if (observation.Code === previousCodeKind) {
                            return {
                                ...observation,
                                Count: (observation?.Count || 1) - 1,
                            };
                        }
                        return observation;
                    }),
                },
            };
        }
        return old;
    });
}

export function updateSummaryCacheOnDelete(params: {
    electricalProjectVersionId: string;
    codeKind: 'Remark' | 'Severe' | 'Serious' | 'Minor' | null | undefined;
}) {
    const { electricalProjectVersionId, codeKind } = params;
    const summaryOptions = elektraAppQueryOptions.project.recapViewOptions({
        electricalProjectVersionId,
    });

    queryClient.setQueryData<SummaryData>(summaryOptions.queryKey, (old) => {
        if (old && old.Summary) {
            return {
                ...old,
                Summary: {
                    ...old.Summary,
                    ElectricalProjectVersionId: old.Summary.ElectricalProjectVersionId,
                    Observations: old?.Summary?.Observations?.map((observation) => {
                        if (observation.Code === codeKind) {
                            return {
                                ...observation,
                                Count: (observation?.Count || 1) - 1,
                            };
                        }
                        return observation;
                    }),
                },
            };
        }
        return old;
    });
}

export function buildOptimisticObservationData(params: {
    body: components['schemas']['CreateElectricalProjectObservationCommand'] | null | undefined;
    classificationData: ClassificationData | undefined;
}): ObservationDataItem | null {
    const { body, classificationData } = params;

    if (!body) {
        return null;
    }

    const severities = [] as ObservationClassificationCodeKinds[];

    body.Reports?.forEach((report) => {
        const foundCode = getClassificationCodeData({
            classificationData,
            body: {
                electricalObservationClassificationGroupId: report.ElectricalObservationClassificationGroupId,
                electricalObservationClassificationCodeId: report.ElectricalObservationClassificationCodeId,
            },
        });
        if (foundCode?.Kind) {
            severities.push(foundCode.Kind);
        }
        else if (report.CodeKind) {
            severities.push(report.CodeKind);
        }
    });

    const highestCodeKind = severities?.sort((a, b) => {
        const severityOrder = ['Severe', 'Serious', 'Minor', 'Remark'];
        return severityOrder.indexOf(a) - severityOrder.indexOf(b);
    })?.[0] || null;

    const codeClassification: {
        ElectricalObservationClassificationCodeId?: string | null;
        Kind?: ObservationClassificationCodeKinds | null;
        HexColorCode?: string | null;
    } | null | undefined = {
        HexColorCode: OBSERVATION_COLOR_CODES[highestCodeKind as keyof typeof OBSERVATION_COLOR_CODES],
        Kind: highestCodeKind || null,
    };

    const { FloorplanElectricalProjectFileId, FloorplanPinCoordinateX, FloorplanPinCoordinateY } = body;

    return {
        ...body,
        ElectricalProjectObservationId: body.ElectricalProjectObservationId,
        Code: codeClassification,
        ElectricalProjectDistributionBoardId: isDistributionBoardObservationKind(body.ObjectKind)
            ? body.ObjectId
            : null,
        ElectricalProjectFinalCircuitId: isFinalCircuitObservationKind(body.ObjectKind) ? body.ObjectId : null,
        ElectricalProjectDistributionBoardDeviceId: body.ObjectKind === 'DistributionBoardDevice' ? body.ObjectId : null,
        ElectricalProjectDistributionBoardFileId: body.ObjectKind === 'DistributionBoardFile' ? body.ObjectId : null,
        ElectricalProjectCheckListItemId: body.ObjectKind === 'CheckListItem' ? body.ObjectId : null,
        ElectricalProjectRisoMeasurementId: body.ObjectKind === 'RisoMeasurement' ? body.ObjectId : null,
        ElectricalProjectPVInverterId: body.ObjectKind === 'PVInverter' ? body.ObjectId : null,
        FloorplanFile: FloorplanElectricalProjectFileId
            ? {
                    ElectricalProjectFileId: FloorplanElectricalProjectFileId,
                    PinCoordinateX: FloorplanPinCoordinateX || null,
                    PinCoordinateY: FloorplanPinCoordinateY || null,
                }
            : null,
        CodeKind: codeClassification?.Kind || null,
        Reports: body.Reports?.map((report) => {
            const reportClassificationCode = getClassificationCodeData({
                classificationData,
                body: {
                    electricalObservationClassificationGroupId: report.ElectricalObservationClassificationGroupId,
                    electricalObservationClassificationCodeId: report.ElectricalObservationClassificationCodeId,
                },
            });

            return {
                ...report,
                Group: getClassificationGroupData({
                    classificationData,
                    electricalObservationClassificationGroupId: report.ElectricalObservationClassificationGroupId,
                }),
                Code: reportClassificationCode,
                CodeKind: reportClassificationCode?.Kind || null,
            };
        }) || [],
    };
}
